import React, { useEffect, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import {
  Box,
  Button,
  Combobox,
  DatePicker,
  EmptyState,
  Box as FormRow,
  Input,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useWalletSearchQuery } from 'hooks/queries/useWalletSearchQuery'
import { usePaginationNextPageItemCount } from 'hooks/usePaginationNextPageItemCount'
import { TransactionsPagination } from 'pages/WalletDetails/components/RecentActivity/TransactionsPagination'
import { sanitizeUserInput } from 'utils/utils'
import SearchTransactionTable from './SearchTransactionTable'

const DEFAULT_NUMBER_OF_ROWS = '10'

interface SearchWalletTableProps {
  pageId: string
}

export const searchWalletTableTags = {
  DASHBOARD_RECENT_WALLETS: {
    id: 'DASHBOARD_RECENT_WALLETS',
    pageName: 'Home Page',
    skeletonTableRow: 15,
    limitReturnedRowsTo: 8,
  },
  WALLETS_TRANSACTION_HISTORY: {
    id: 'WALLETS_TRANSACTION_HISTORY',
    pageName: 'Wallets Page',
    skeletonTableRow: 4,
    limitReturnedRowsTo: 15,
  },
}

export const SearchWalletTable = ({ pageId }: SearchWalletTableProps) => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()

  const [searchTermQ, setSearchTermQ] = useState<string | undefined>('')

  const [dateValue, setDateValue] = useState<DateRange>({
    from: null,
    to: null,
  })
  const [dateIntervalChanged, setDateIntervalChanged] = useState<Boolean>(false)

  const [selectedOptions, setSelectedOptions] = useState<string[]>([])

  const [page, setPage] = useState(0)
  const [searchPage, setSearchPage] = useState(0)
  const [offset, setOffset] = useState(0)
  const [numberOfRows, setNumberOfRows] = useState(DEFAULT_NUMBER_OF_ROWS)

  const pageName = searchWalletTableTags[pageId].pageName

  const walletIsBeingSearched = !!(
    searchTermQ.length ||
    selectedOptions.length ||
    dateIntervalChanged
  )

  const {
    data: walletTransactions,
    isFetching,
    isError,
    isSuccess,
  } = useWalletSearchQuery({
    accountId: currentAccount?.id,
    searchTerm: useDebounce(searchTermQ, 1000),
    startDate: dateValue.from,
    endDate: dateValue.to,
    aggregation: useDebounce(selectedOptions, 1000),
    pageSize: numberOfRows,
    pageNumber: walletIsBeingSearched ? searchPage : page,
  })

  const paging = {
    total: walletTransactions?.transactions?.totalElements,
    offset,
    max: walletTransactions?.transactions?.pageable?.pageSize,
  }

  const nextPageRowCount = usePaginationNextPageItemCount({
    offset: paging?.offset,
    page: paging?.offset,
    total: paging?.total,
    max: paging?.max,
  })

  const onSearchQueryChanged = e => {
    const { value } = e?.target ?? ''
    setSearchTermQ(sanitizeUserInput(value))
  }

  const onDateSelected = (dateVal: DateRange) => {
    setDateValue(dateVal)
    setDateIntervalChanged(true)
  }

  const clearFilters = () => {
    setPage(0)
    setSearchPage(0)
    setSearchTermQ('')
    setSelectedOptions([])
    setDateValue({
      from: null,
      to: null,
    })
  }

  const hasWalletTransactions = !!walletTransactions?.transactions?.content?.length

  useEffect(() => {
    // Set dateIntervalChanged value to false for null date values
    !dateValue.from && !dateValue.to && setDateIntervalChanged(false)
  }, [walletTransactions, walletIsBeingSearched, pageName])

  useEffect(() => {
    if (walletIsBeingSearched) {
      setSearchPage(0)
    }
  }, [walletIsBeingSearched])

  const shouldDisplayPaginator =
    pageId === searchWalletTableTags.WALLETS_TRANSACTION_HISTORY.id &&
    !!paging?.total &&
    numberOfRows &&
    !isError

  return (
    <div data-testid="recent-activity-screen">
      <Box paddingB={8} gapX={12} flex isFlexWrap>
        <FormRow
          testId="transaction-text-search-input"
          width="full"
          className="pb-4 lg:flex-auto lg:w-60"
          gapY={12}
        >
          <Input
            testId="network-member-search-field"
            onChange={onSearchQueryChanged}
            searchable={true}
            value={searchTermQ}
            placeholderText={t('wallets.searchPlaceholderText')}
          />
        </FormRow>
        <FormRow
          testId="transaction-date-search-input"
          width="full"
          className="pb-4 lg:flex-auto lg:w-24"
          gapY={12}
        >
          <DatePicker
            disabled={isFetching}
            label=""
            placeholder={t('wallets.selectDate')}
            initialSelected={dateValue}
            onSelect={dateVal => onDateSelected(dateVal)}
          />
        </FormRow>
        <FormRow
          testId="transaction-type-search-input"
          flex
          width="full"
          className="pb-4 lg:flex-auto lg:w-16"
        >
          <FormRow className="w-auto mt-[-4px]" style={{ width: '220px' }} paddingR={8}>
            <Combobox
              id="transaction-type-search-input"
              testId="transaction-type-search-input"
              searchPlaceholder={t('wallets.transactionTypePlaceholderText')}
              disabled={isFetching}
              items={
                walletTransactions?.aggregations?.map(aggregation => ({
                  value: aggregation.value,
                  leftText: aggregation.label,
                })) ?? []
              }
              selectPlaceholder={t('wallets.transactionTypePlaceholderText')}
              checkedValues={selectedOptions}
              onCheckedChange={value => {
                setSelectedOptions(current => {
                  if (current?.includes(value)) {
                    return current?.filter(item => item !== value)
                  }
                  return [...current, value]
                })
              }}
            />
          </FormRow>
          {walletIsBeingSearched && !isFetching && (
            <Button
              testId="clear-search-filters-btn"
              variant="ghost"
              noPadding
              onClick={clearFilters}
            >
              {t('clearFilters')}
            </Button>
          )}
        </FormRow>
      </Box>

      {!isError && walletIsBeingSearched && !isFetching && !hasWalletTransactions && (
        <EmptyState
          testId="transaction-search-status-card"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('wallets.noSearchResultHeadline')}
          description={
            <>
              {t('wallets.noSearchResultDescription')} {` `}
              <Button
                testId="transaction-search-clear-result-btn"
                variant="ghost"
                noPadding
                onClick={clearFilters}
              >
                {t('wallets.clearSearchCriteria')}
              </Button>
            </>
          }
        />
      )}

      {!isError && !walletIsBeingSearched && !isFetching && !hasWalletTransactions && (
        <EmptyState
          LottiePlayer={<Lottie src="no-data" />}
          testId="transaction-search-no-transactions-label"
          title={t('wallets.emptyStateHeadline')}
          description={t('wallets.noTransactionDescription')}
          PrimaryButton={<Button onClick={clearFilters}>{t('wallets.goToWallets')}</Button>}
        />
      )}

      {!isError && isFetching && <RowsSkeletonLoader rows={nextPageRowCount} />}

      {isSuccess && !isFetching && hasWalletTransactions && (
        <SearchTransactionTable walletTransactions={walletTransactions} />
      )}

      {shouldDisplayPaginator && (
        <TransactionsPagination
          page={walletIsBeingSearched ? searchPage : page}
          setPage={walletIsBeingSearched ? setSearchPage : setPage}
          setOffset={setOffset}
          pagination={paging}
          nrOfRows={numberOfRows}
          setNrOfRows={setNumberOfRows}
        />
      )}

      {isError && (
        <EmptyState
          LottiePlayer={<Lottie src="connection-error" />}
          testId="search-wallet-table-error"
          title={t('oopsSomethingWentWrong')}
        />
      )}
    </div>
  )
}
